import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  UserPlus, Trash2, Edit2, X, Check, Shield, AlertCircle,
  BookOpen, GraduationCap, Building2, Globe, Search, Eye, EyeOff,
  Mail, Copy, Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

const PERMISSION_CONFIG = [
  { key: 'student_exam', label: 'Student Exam Access', desc: 'Can take practice exams', icon: BookOpen },
  { key: 'teacher_dashboard', label: 'Teacher Dashboard', desc: 'View class results, grade work', icon: GraduationCap },
  { key: 'teacher_resources', label: 'Teacher Resources', desc: 'Parent Guide, Answer Keys', icon: Shield },
  { key: 'school_admin_dashboard', label: 'School Admin', desc: 'School-level stats & management', icon: Building2 },
  { key: 'district_admin_dashboard', label: 'District Admin', desc: 'Multi-school view & analytics', icon: Globe },
  { key: 'screener_access', label: 'Screener Access', desc: 'Administer WIDA Screener', icon: Search },
];

const UserManagement = ({ initialRoleFilter = '' }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [roleFilter, setRoleFilter] = useState(initialRoleFilter);
  const [form, setForm] = useState({
    email: '', password: '', name: '', role: 'teacher',
    permissions: {
      student_exam: true, teacher_dashboard: true, teacher_resources: true,
      school_admin_dashboard: false, district_admin_dashboard: false, screener_access: true,
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Track passwords for sharing (not stored on server)
  const [userPasswords, setUserPasswords] = useState({});

  const buildCredentialMessage = (user, password) => {
    const perms = user.permissions || {};
    const enabledPerms = Object.entries(perms)
      .filter(([_, v]) => v)
      .map(([k]) => k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
      .join(', ');
    
    return [
      `Hi ${user.name},`,
      ``,
      `You've been given access to the MOSAIC Assessment Platform for preview and testing.`,
      ``,
      `LOGIN URL: ${APP_URL}/login`,
      ``,
      `Your credentials:`,
      `  Email: ${user.email}`,
      `  Password: ${password}`,
      ``,
      `Your access includes: ${enabledPerms || 'Student Exam'}`,
      `Role: ${user.role}`,
      ``,
      `Quick start:`,
      `1. Go to ${APP_URL}/login`,
      `2. Click "Sign in with Email & Password"`,
      `3. Enter your credentials above`,
      `4. Explore the platform and note any feedback`,
      ``,
      `Please do not share these credentials. Your account can be disabled at any time.`,
      ``,
      `Thank you,`,
      `MOSAIC Team`
    ].join('\n');
  };

  const handleCopyCredentials = (user, password) => {
    const message = buildCredentialMessage(user, password);
    navigator.clipboard.writeText(message).then(() => {
      toast.success(`Credentials copied to clipboard for ${user.name}`);
    }).catch(() => {
      // Fallback: select text
      const textarea = document.createElement('textarea');
      textarea.value = message;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(`Credentials copied for ${user.name}`);
    });
  };

  const handleEmailCredentials = (user, password) => {
    const subject = encodeURIComponent(`Your MOSAIC Platform Access Credentials`);
    const body = encodeURIComponent(buildCredentialMessage(user, password));
    window.location.href = `mailto:${user.email}?subject=${subject}&body=${body}`;
    toast.success(`Opening email client for ${user.email}...`);
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/managed-users`);
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);
  useEffect(() => { setRoleFilter(initialRoleFilter); }, [initialRoleFilter]);

  const resetForm = () => {
    setForm({
      email: '', password: '', name: '', role: 'teacher',
      permissions: {
        student_exam: true, teacher_dashboard: true, teacher_resources: true,
        school_admin_dashboard: false, district_admin_dashboard: false, screener_access: true,
      }
    });
    setShowCreate(false);
    setEditingUser(null);
    setShowPassword(false);
  };

  const handleCreate = async () => {
    if (!form.email.trim() || !form.password || !form.name.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/managed-users`, form);
      const createdUser = res.data.user;
      // Store password for sharing (only kept in memory, never persisted)
      setUserPasswords(prev => ({ ...prev, [createdUser.user_id]: form.password }));
      toast.success(`User ${form.email} created successfully`);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    setSubmitting(true);
    const updates = {
      name: form.name,
      role: form.role,
      permissions: form.permissions,
    };
    if (form.password) {
      updates.password = form.password;
    }
    try {
      await axios.put(`${API}/admin/managed-users/${editingUser.user_id}`, updates);
      // If password was changed, track it
      if (form.password) {
        setUserPasswords(prev => ({ ...prev, [editingUser.user_id]: form.password }));
      }
      toast.success(`User ${editingUser.email} updated`);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/managed-users/${user.user_id}`, 
        { is_active: !user.is_active });
      toast.success(`User ${user.email} ${user.is_active ? 'disabled' : 'enabled'}`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/managed-users/${user.user_id}`);
      toast.success(`User ${user.email} deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setShowCreate(true);
    setForm({
      email: user.email,
      password: '',
      name: user.name,
      role: user.role,
      permissions: user.permissions || {
        student_exam: true, teacher_dashboard: false, teacher_resources: false,
        school_admin_dashboard: false, district_admin_dashboard: false, screener_access: true,
      }
    });
  };

  const applyPreset = (preset) => {
    const presets = {
      student: { student_exam: true, teacher_dashboard: false, teacher_resources: false, school_admin_dashboard: false, district_admin_dashboard: false, screener_access: false },
      teacher: { student_exam: true, teacher_dashboard: true, teacher_resources: true, school_admin_dashboard: false, district_admin_dashboard: false, screener_access: true },
      full: { student_exam: true, teacher_dashboard: true, teacher_resources: true, school_admin_dashboard: true, district_admin_dashboard: true, screener_access: true },
    };
    setForm(f => ({ ...f, permissions: presets[preset], role: preset === 'student' ? 'student' : preset === 'full' ? 'district_admin' : 'teacher' }));
  };

  return (
    <div data-testid="user-management-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Managed Users</h2>
          <p className="text-sm text-gray-500">{users.length} test/preview accounts</p>
        </div>
        {!showCreate && (
          <Button onClick={() => { resetForm(); setShowCreate(true); }} className="btn-sharp-primary h-11" data-testid="create-user-btn">
            <UserPlus size={18} className="mr-2" />
            Create User
          </Button>
        )}
      </div>

      {/* Create/Edit Form */}
      {showCreate && (
        <div className="border-2 border-[#002FA7] bg-blue-50 p-6 mb-6" data-testid="user-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg">
              {editingUser ? `Edit: ${editingUser.email}` : 'Create New User'}
            </h3>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              <X size={18} />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="text-sm font-semibold mb-1 block">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Display name"
                className="h-11 border-2 rounded-none"
                data-testid="user-form-name"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">Email *</Label>
              <Input
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="user@email.com"
                disabled={!!editingUser}
                className="h-11 border-2 rounded-none"
                data-testid="user-form-email"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">
                Password {editingUser ? '(leave blank to keep current)' : '*'}
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder={editingUser ? 'New password (optional)' : 'Min 6 characters'}
                  className="h-11 border-2 rounded-none pr-10"
                  data-testid="user-form-password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1 block">Base Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger className="h-11 border-2 rounded-none" data-testid="user-form-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="school_admin">School Admin</SelectItem>
                  <SelectItem value="district_admin">District Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Permission Presets */}
          <div className="mb-4">
            <Label className="text-sm font-semibold mb-2 block">Quick Presets</Label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="btn-sharp text-xs" onClick={() => applyPreset('student')} data-testid="preset-student">
                Student Only
              </Button>
              <Button variant="outline" size="sm" className="btn-sharp text-xs" onClick={() => applyPreset('teacher')} data-testid="preset-teacher">
                Teacher View
              </Button>
              <Button variant="outline" size="sm" className="btn-sharp text-xs" onClick={() => applyPreset('full')} data-testid="preset-full">
                Full Access
              </Button>
            </div>
          </div>

          {/* Permission Toggles */}
          <div className="mb-6">
            <Label className="text-sm font-semibold mb-2 block">Access Permissions</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PERMISSION_CONFIG.map(({ key, label, desc, icon: Icon }) => (
                <div key={key} className="flex items-center justify-between p-3 border border-gray-200 bg-white" data-testid={`perm-toggle-${key}`}>
                  <div className="flex items-center gap-3">
                    <Icon size={16} className="text-gray-500" />
                    <div>
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-xs text-gray-400">{desc}</div>
                    </div>
                  </div>
                  <Switch
                    checked={form.permissions[key] || false}
                    onCheckedChange={(checked) => setForm(f => ({
                      ...f,
                      permissions: { ...f.permissions, [key]: checked }
                    }))}
                    data-testid={`perm-switch-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={editingUser ? handleUpdate : handleCreate}
              disabled={submitting}
              className="btn-sharp-primary h-11 px-6"
              data-testid="user-form-submit"
            >
              {submitting ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
            </Button>
            <Button variant="outline" onClick={resetForm} className="btn-sharp h-11">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Users List */}
      {!loading && users.length > 0 && (
        <div className="flex items-center gap-3 mb-3">
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            className="h-9 border-2 border-gray-300 px-3 text-sm"
            data-testid="user-role-filter"
          >
            <option value="">All Roles</option>
            <option value="student">Students</option>
            <option value="teacher">Teachers</option>
            <option value="school_admin">School Admins</option>
            <option value="district_admin">District Admins</option>
          </select>
          <span className="text-xs text-gray-400">
            {roleFilter ? users.filter(u => u.role === roleFilter).length : users.length} users
          </span>
        </div>
      )}
      {loading ? (
        <div className="p-12 text-center"><div className="loading-spinner mx-auto"></div></div>
      ) : users.length === 0 ? (
        <div className="border-2 border-gray-200 p-12 text-center" data-testid="no-users-message">
          <UserPlus className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-2">No managed users yet</p>
          <p className="text-sm text-gray-400">Create test accounts for your development team to preview the app.</p>
        </div>
      ) : (
        <div className="border-2 border-gray-200 divide-y divide-gray-100" data-testid="users-list">
          {users.filter(u => !roleFilter || u.role === roleFilter).map(user => (
            <div key={user.user_id} className={`p-4 flex items-center justify-between ${user.is_active === false ? 'opacity-50 bg-gray-50' : ''}`} data-testid={`user-row-${user.user_id}`}>
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-10 h-10 flex items-center justify-center border-2 font-bold text-sm ${
                  user.is_active === false ? 'border-gray-300 text-gray-400 bg-gray-100' : 'border-[#002FA7] text-[#002FA7] bg-blue-50'
                }`}>
                  {user.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold truncate">{user.name}</div>
                  <div className="text-sm text-gray-500 truncate">{user.email}</div>
                  {user.school_name && (
                    <div className="text-xs text-gray-400 truncate">{user.school_name}</div>
                  )}
                </div>
                <div className={`px-2 py-1 text-xs font-bold border ${
                  user.role === 'district_admin' ? 'bg-purple-50 border-purple-300 text-purple-700' :
                  user.role === 'school_admin' ? 'bg-blue-50 border-blue-300 text-blue-700' :
                  user.role === 'teacher' ? 'bg-green-50 border-green-300 text-green-700' :
                  'bg-gray-50 border-gray-300 text-gray-700'
                }`}>
                  {user.role}
                </div>
                {user.is_active === false && (
                  <div className="px-2 py-1 text-xs font-bold bg-red-50 border border-red-300 text-red-700">
                    DISABLED
                  </div>
                )}
                {/* Permission badges */}
                <div className="hidden md:flex gap-1 flex-wrap">
                  {user.permissions && Object.entries(user.permissions).filter(([_, v]) => v).map(([k]) => (
                    <span key={k} className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 border border-gray-200">
                      {k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).replace('Dashboard', '').trim()}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-4">
                {/* Share credentials buttons */}
                {userPasswords[user.user_id] && user.is_active !== false ? (
                  <>
                    <Button variant="ghost" size="sm" 
                      onClick={() => handleEmailCredentials(user, userPasswords[user.user_id])}
                      title="Send credentials via email"
                      data-testid={`email-creds-${user.user_id}`}>
                      <Send size={16} className="text-[#002FA7]" />
                    </Button>
                    <Button variant="ghost" size="sm" 
                      onClick={() => handleCopyCredentials(user, userPasswords[user.user_id])}
                      title="Copy credentials to clipboard"
                      data-testid={`copy-creds-${user.user_id}`}>
                      <Copy size={16} className="text-[#002FA7]" />
                    </Button>
                    <div className="w-px h-5 bg-gray-200 mx-1"></div>
                  </>
                ) : user.is_active !== false ? (
                  <>
                    <Button variant="ghost" size="sm"
                      onClick={() => {
                        const pwd = window.prompt(`Set a new password for ${user.email} to enable sharing.\nMin 6 characters:`);
                        if (pwd && pwd.length >= 6) {
                          axios.put(`${API}/admin/managed-users/${user.user_id}`, { password: pwd })
                            .then(() => {
                              setUserPasswords(prev => ({ ...prev, [user.user_id]: pwd }));
                              toast.success(`Password updated. You can now share credentials.`);
                            })
                            .catch(() => toast.error('Failed to update password'));
                        } else if (pwd) {
                          toast.error('Password must be at least 6 characters');
                        }
                      }}
                      title="Set password to enable sharing"
                      data-testid={`set-password-${user.user_id}`}>
                      <Mail size={16} className="text-gray-400" />
                    </Button>
                    <div className="w-px h-5 bg-gray-200 mx-1"></div>
                  </>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => handleToggleActive(user)}
                  className={user.is_active === false ? 'text-green-600 hover:text-green-700' : 'text-yellow-600 hover:text-yellow-700'}
                  title={user.is_active === false ? 'Enable user' : 'Disable user'}
                  data-testid={`toggle-active-${user.user_id}`}>
                  {user.is_active === false ? <Check size={16} /> : <AlertCircle size={16} />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => startEdit(user)} title="Edit user" data-testid={`edit-user-${user.user_id}`}>
                  <Edit2 size={16} className="text-gray-500" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} title="Delete user" data-testid={`delete-user-${user.user_id}`}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info */}
      <div className="mt-6 bg-blue-50 border-2 border-blue-200 p-4 text-sm text-blue-700">
        <div className="flex gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-1">About Managed Users</p>
            <p>These accounts use email/password login (not Google SSO). Share credentials with your team for preview access. 
            You can disable or delete accounts at any time. Disabled accounts are immediately logged out.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
